import { useCallback, useRef, useEffect } from 'react';
import { AgentService } from '@/services/agent';
import { useAgentStore } from '@/stores/agentStore';
import { useReferenceStore } from '@/stores/referenceStore';
import { createLocalId, buildAssistantIntro, stripThinkingAndToolCallTags } from '../helpers';
import { Message } from '../types';
import { useStreamPolling } from './useStreamPolling';
import { useAgentMessages } from './useAgentMessages';
import { useStreamChunkHandler } from './useStreamChunkHandler';
import { useStreamCompletionHandler } from './useStreamCompletionHandler';
import { useStreamErrorHandler } from './useStreamErrorHandler';
import { useStreamSession, buildUserMessage, processAttachments } from './useStreamSession';
import {
  createOnAgentIdCallback,
  createOnIntentCallback,
  createOnThinkingCallback,
  createOnCollabStatusCallback,
  createOnSubThinkingCallback,
  createOnSubOutputCallback,
  createOnGenerationConfirmCallback,
  createOnSearchResultCallback,
} from './useStreamCallbacks';

export function useStreamHandler() {
  const { addActiveProcessLog, failLatestActiveProcessLog, finalizeAssistantMessage, updateMessage } = useAgentMessages();
  const { handleChunk } = useStreamChunkHandler();
  const { handleComplete } = useStreamCompletionHandler();
  const { handleError } = useStreamErrorHandler();
  const { prepareSendMessage, buildContextPrefix, initStreamState } = useStreamSession();

  // 🔵 流控制标志位现在从 store 读取（替代原来的 useRef）
  // 这些状态是响应式的，且会在 resetStreamingState 时统一重置
  const accumulatedTextRef = useRef('');
  const refreshChatHistoryRef = useRef<() => void>(() => {});
  const dispatchTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const { startPolling: startStreamPolling, stopAllPolling } = useStreamPolling({
    onComplete: (_content, _msgId) => {
      const store = useAgentStore.getState();
      store.resetStreamingState();
      if (store.sessionId) {
        store.setSelectedHistoryId(store.sessionId);
      }
      refreshChatHistoryRef.current();
    },
    onFailed: (error, _msgId) => {
      failLatestActiveProcessLog('后台生成失败', error || '未知错误');
      useAgentStore.getState().resetStreamingState();
    },
  });

  const handleStopGeneration = useCallback(async () => {
    const store = useAgentStore.getState();
    // 🔵 标记流式会话已结束，防止 abort 后搜索/确认回调重新初始化 builder
    useAgentStore.setState({ streamEnded: true });
    // 🔴 F-11: 清除 dispatchTimeoutIdRef，防止停止后超时回调仍触发
    if (dispatchTimeoutIdRef.current) {
      clearTimeout(dispatchTimeoutIdRef.current);
      dispatchTimeoutIdRef.current = null;
    }
    if (store.abortController) {
      store.abortController.abort();
      store.setAbortController(null);
    }

    const builder = store.activeReplyBuilder;
    const currentLogs = store.activeProcessLogs;

    // 🔵 v3-#8: 停止路径统一走 finalizeAssistantMessage，确保后端同步和 introText 一致
    // 🔴 F-9: 以 builder.type 判断场景，不依赖 searchData/generationOptions 是否存在
    // 修复：builder.type='search' 但 searchData=null 时，仍应走搜索分支（显示"搜索已停止"）
    if (builder) {
      const msgType = builder.type || 'text';
      const msgId = builder.id || createLocalId();

      if (msgType === 'search') {
        addActiveProcessLog('result', '已停止搜索', builder.searchData ? '保留已获取的搜索结果' : '搜索未完成');
        await finalizeAssistantMessage({
          id: msgId,
          type: 'search',
          text: builder.text || (builder.searchData ? '搜索结果（已停止）' : '搜索已停止'),
          searchData: builder.searchData,
          introText: builder.introText || undefined,
          skipBackendId: true,
        } as any);
        return;
      }

      if (msgType === 'generation_confirm') {
        addActiveProcessLog('result', '已停止生成', builder.generationOptions ? '保留生成确认信息' : '生成确认未完成');
        await finalizeAssistantMessage({
          id: msgId,
          type: 'generation_confirm',
          text: builder.text || (builder.generationOptions ? '请确认生成参数' : '生成确认已停止'),
          generationOptions: builder.generationOptions,
          pendingUserText: (builder.metadata || {}).pendingUserText,
          pendingAttachments: (builder.metadata || {}).pendingAttachments,
          pendingReferences: (builder.metadata || {}).pendingReferences,
          pendingRefNodeIds: (builder.metadata || {}).pendingRefNodeIds,
          introText: builder.introText || undefined,
          skipBackendId: true,
        } as any);
        return;
      }
    }

    // === 常规文本场景：走原有逻辑 ===
    if (store.streamingText) {
      addActiveProcessLog('result', '已停止生成', '保留当前已生成的内容');
      await finalizeAssistantMessage({
        // 🔴 v6-#10: 传入 id，避免 builder 为空时 finalId 为 undefined
        id: builder?.id || undefined,
        text: store.streamingText,
        type: 'text',
      });
      return;
    }
    addActiveProcessLog('result', '已停止本次请求');
    await finalizeAssistantMessage({
      id: builder?.id || createLocalId(),
      text: '已停止本次请求。',
      type: 'text',
    });
  }, [addActiveProcessLog, finalizeAssistantMessage]);

  const handleSendMessage = useCallback(async () => {
    // 🔴 v8-#6: 发送新消息前停止所有 polling，避免旧 polling 完成后干扰新消息
    stopAllPolling();

    const prepared = await prepareSendMessage();
    if (!prepared) return;

    const { input: currentInput, messages: currentMessages, projectId: currentProjectId } = prepared;

    const userText = currentInput;
    useAgentStore.getState().setInput('');

    const currentRefs = useReferenceStore.getState().referencedNodes;
    const currentAttachments = [...useAgentStore.getState().attachments];

    const userMessage = buildUserMessage(userText, currentAttachments, currentRefs);
    const newMessages: Message[] = [...currentMessages, userMessage];
    useAgentStore.getState().setMessages(newMessages);
    useAgentStore.getState().setAttachments([]);
    useReferenceStore.getState().clearReferences();

    const initialIntro = buildAssistantIntro({
      userText,
      hasAttachments: currentAttachments.length > 0,
      hasReferences: currentRefs.length > 0,
    });
    initStreamState(userText, currentAttachments.length > 0, currentRefs.length > 0, initialIntro);

    // 🔵 P2: 统一初始化 ActiveReplyBuilder（P1-5: 标志位已移除，initActiveReplyBuilder 本身已足够）
    // 所有新消息都从一个干净的 builder 开始，builder 为 null 表示当前不在流式回复中
    const s = useAgentStore.getState();
    s.initActiveReplyBuilder();
    accumulatedTextRef.current = '';

    try {
        const currentModelId = useAgentStore.getState().currentModelId;
        const { attachmentPayloads, referencePayloads } = await processAttachments(currentAttachments, currentRefs);

        addActiveProcessLog('context', '🧭 正在调度', '分析任务类型，选择最合适的专业智能体', 'active');

        const userContent = buildContextPrefix(currentRefs, currentAttachments, userText);

        const chatMessages = [
          ...currentMessages.map(m => ({ role: m.role, content: m.text })),
          { role: 'user' as const, content: userContent },
        ];

        let accumulatedText = '';
        let accumulatedThinking = '';
        let lastParsedLength = 0;
        const streamSessionId = useAgentStore.getState().sessionId || '';
        let currentIntent: { name: string; confidence: number; source: string } | null = null;
        let pendingBackendMsgId: string | null = null;
        
        // 🔵 v3-#11: 标志位已移除，builder.type 统一判断场景，initActiveReplyBuilder 已足够
        
        // 设置调度超时：30 秒后自动更新状态
        const onAgentId = createOnAgentIdCallback({ addActiveProcessLog });
        // 包装 onAgentId，在收到 agent_id 时清除超时
        const originalOnAgentId = onAgentId;
        const wrappedOnAgentId = (agentId: string) => {
          if (dispatchTimeoutIdRef.current) {
            clearTimeout(dispatchTimeoutIdRef.current);
            dispatchTimeoutIdRef.current = null;
          }
          originalOnAgentId(agentId);
        };
        
        const onIntent = createOnIntentCallback({
          userText, currentAttachments, currentRefs,
          setCurrentIntent: (intent) => { currentIntent = intent; },
        });
        const onThinking = createOnThinkingCallback({ addActiveProcessLog });
        const onCollabStatus = createOnCollabStatusCallback();
        const onSubThinking = createOnSubThinkingCallback();
        const onSubOutput = createOnSubOutputCallback();
        const onGenerationConfirm = createOnGenerationConfirmCallback({
          userText,
          currentAttachments,
          currentRefs,
          attachmentPayloads,
          referencePayloads,
        });
        const onSearchResult = createOnSearchResultCallback({
          getBackendAssistantMsgId: () => pendingBackendMsgId,
        });
        
        // 设置调度超时：30 秒后自动更新状态
        dispatchTimeoutIdRef.current = setTimeout(() => {
          console.warn('[useStreamHandler] Dispatch timeout - auto-updating state');
          const store = useAgentStore.getState();
          store.updateActiveProcessLogs((prev) => prev.map(l =>
            l.title === '🧭 正在调度' && l.status === 'active'
              ? { 
                  ...l, 
                  status: 'completed', 
                  title: '🧭 调度超时', 
                  detail: '调度时间过长，已自动切换到默认智能体' 
                }
              : l
          ));
        }, 30000);

        const controller = await AgentService.chatStream(
          chatMessages,
          (chunk: string) => {
            const store = useAgentStore.getState();
            const result = handleChunk(chunk, {
              accumulatedText,
              accumulatedThinking,
              lastParsedLength,
              streamSessionId,
              // 🔵 P1-5: 标志位已移除，chunk handler 内部直接读取 store.activeReplyBuilder.type
            });
            accumulatedText = result.accumulatedText;
            accumulatedTextRef.current = result.accumulatedText;
            lastParsedLength = result.lastParsedLength;

            // 🔴 F-4: 移除独立的 updateActiveReplyBuilder({ text }) 路径
            // P0-1 已将 setStreamingText 改为同时写入 builder.text，
            // 此处不再需要独立更新 builder.text，避免双源驱动不一致
            // setStreamingText 在 agentStore.ts 中已处理 builder.text 同步
          },
          async () => {
            if (dispatchTimeoutIdRef.current) {
              clearTimeout(dispatchTimeoutIdRef.current);
              dispatchTimeoutIdRef.current = null;
            }
            await handleComplete({
              accumulatedText,
              accumulatedThinking,
              currentIntent,
              userText,
              currentAttachments,
              currentRefs,
              currentModelId,
              streamSessionId,
              refreshChatHistory: () => refreshChatHistoryRef.current(),
            });
          },
          async (error: string) => {
            if (dispatchTimeoutIdRef.current) {
              clearTimeout(dispatchTimeoutIdRef.current);
              dispatchTimeoutIdRef.current = null;
            }
            await handleError(error, {
              accumulatedText,
              currentIntent,
              userText,
              currentAttachments,
              currentRefs,
              streamSessionId,
              startStreamPolling,
            });
          },
          {
            modelId: currentModelId,
            sessionId: streamSessionId || undefined,
            projectId: currentProjectId || undefined,
            attachments: attachmentPayloads,
            references: referencePayloads,
            searchMode: useAgentStore.getState().searchMode,
            agentId: useAgentStore.getState().currentAgentId || undefined,
            onMessageIds: (ids) => {
              const oldId = pendingBackendMsgId;
              pendingBackendMsgId = ids.assistant_msg_id;
              const store = useAgentStore.getState();
              store.setPendingBackendMsgIds(ids);

              // 🔵 P2: 优先把后端消息 ID 同步到 ActiveReplyBuilder
              if (ids.assistant_msg_id && store.activeReplyBuilder) {
                store.updateActiveReplyBuilder({ id: ids.assistant_msg_id });
              }

              // 🔵 #21: pendingLocalMsgIds 已移除，不再需要本地 ID → 后端 ID 替换逻辑
              // builder 架构下消息 ID 统一在 builder.id 中维护
            },
            onAgentId: wrappedOnAgentId,
            onIntent,
            onThinking,
            onCollabStatus,
            onSubThinking,
            onSubOutput,
            onGenerationConfirm,
            onSearchResult,
          }
        );

        useAgentStore.getState().setAbortController(controller);
    } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Generation aborted by user');
        } else {
          await finalizeAssistantMessage({
            id: createLocalId(),
            text: `连接错误，请稍后重试。${error.message || ''}`,
            type: 'text',
          });
          return;
        }
        useAgentStore.getState().resetStreamingState();
    }
  }, [addActiveProcessLog, finalizeAssistantMessage, handleChunk, handleComplete, handleError, startStreamPolling, stopAllPolling, prepareSendMessage, buildContextPrefix, initStreamState]);

  useEffect(() => {
    return () => {
      const controller = useAgentStore.getState().abortController;
      if (controller) {
        controller.abort();
        useAgentStore.getState().setAbortController(null);
      }
      // 🔴 v7-#7: 清理 dispatchTimeoutIdRef，避免组件卸载后定时器仍触发
      if (dispatchTimeoutIdRef.current) {
        clearTimeout(dispatchTimeoutIdRef.current);
        dispatchTimeoutIdRef.current = null;
      }
    };
  }, []);

  return {
    handleSendMessage,
    handleStopGeneration,
    refreshChatHistoryRef,
  };
}
